import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import AsyncSelect from 'react-select/async';
import {
  StickyNote,
  Plus,
  Pencil,
  Trash2,
  Search,
  User,
  Phone,
  Store,
  Sparkles,
  Filter,
  X,
  Package,
  Printer,
  Archive
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { authAPI, teamNotesAPI, salesOrdersAPI } from '../services/api';
import { formatCurrency } from '../utils/currency';
import { generateNoteTicketPDF } from '../utils/noteTicketPDF';
import {
  ModernButton,
  ModernInput,
  ModernModal,
  ModernLoadingSpinner
} from '../components/ui/ModernComponents';

const TZ_CASA = 'Africa/Casablanca';

/** Couleurs « post-it » + dégradé papier */
const NOTE_THEME = {
  general: {
    label: 'Général',
    paper: 'from-[#fffef0] via-[#fef9c3] to-[#fde68a]',
    shadow: 'shadow-[6px_8px_24px_rgba(45,35,20,0.22),2px_3px_0_rgba(255,255,255,0.5)_inset]',
    badge: 'bg-amber-900/15 text-amber-950 border-amber-900/20',
    pin: 'from-amber-600 to-amber-800'
  },
  sav: {
    label: 'SAV',
    paper: 'from-[#fff5f7] via-[#fce7f3] to-[#fbcfe8]',
    shadow: 'shadow-[6px_8px_24px_rgba(80,20,40,0.2),2px_3px_0_rgba(255,255,255,0.45)_inset]',
    badge: 'bg-rose-900/15 text-rose-950 border-rose-900/20',
    pin: 'from-rose-500 to-rose-800'
  },
  rdv: {
    label: 'Rendez-vous',
    paper: 'from-[#f5f3ff] via-[#ede9fe] to-[#ddd6fe]',
    shadow: 'shadow-[6px_8px_24px_rgba(40,30,90,0.2),2px_3px_0_rgba(255,255,255,0.45)_inset]',
    badge: 'bg-violet-900/15 text-violet-950 border-violet-900/20',
    pin: 'from-violet-500 to-violet-800'
  },
  client: {
    label: 'Client',
    paper: 'from-[#f0f9ff] via-[#e0f2fe] to-[#bae6fd]',
    shadow: 'shadow-[6px_8px_24px_rgba(15,50,80,0.2),2px_3px_0_rgba(255,255,255,0.5)_inset]',
    badge: 'bg-sky-900/15 text-sky-950 border-sky-900/20',
    pin: 'from-sky-500 to-sky-800'
  },
  interne: {
    label: 'Interne',
    paper: 'from-[#fffbeb] via-[#fef3c7] to-[#fde68a]',
    shadow: 'shadow-[6px_8px_24px_rgba(60,40,10,0.2),2px_3px_0_rgba(255,255,255,0.45)_inset]',
    badge: 'bg-amber-900/15 text-amber-950 border-amber-900/20',
    pin: 'from-orange-500 to-orange-700'
  }
};

const CATEGORY_ORDER = ['general', 'sav', 'rdv', 'client', 'interne'];

const NOTE_STATUS = {
  pas_encore: {
    label: 'Pas encore',
    badge: 'bg-slate-100 text-slate-800 border-slate-300',
  },
  en_cours: {
    label: 'En cours',
    badge: 'bg-sky-100 text-sky-900 border-sky-300',
  },
  traite: {
    label: 'Traité',
    badge: 'bg-emerald-100 text-emerald-900 border-emerald-300',
  },
};

const NOTE_STATUS_ORDER = ['pas_encore', 'en_cours', 'traite'];

const BOARD_BG = {
  backgroundColor: '#6b5344',
  backgroundImage: `
    radial-gradient(ellipse 120% 80% at 50% -20%, rgba(255,255,255,0.12), transparent 55%),
    radial-gradient(circle at 15% 25%, rgba(0,0,0,0.04) 1.5px, transparent 1.5px),
    radial-gradient(circle at 85% 55%, rgba(255,255,255,0.06) 1px, transparent 1px),
    linear-gradient(165deg, #7d5e48 0%, #5c4636 45%, #4a3829 100%)
  `,
  backgroundSize: '100% 100%, 28px 28px, 22px 22px, 100% 100%'
};

const emptyForm = {
  title: '',
  body: '',
  category: 'general',
  status: 'pas_encore',
  client_name: '',
  client_phone: '',
  sales_order_id: '',
  store_id: '',
  is_pinned: false
};

function orderToSelectOption(o) {
  if (!o || o.id == null) return null;
  return {
    value: String(o.id),
    label: `#${o.id} — ${o.client_name || 'Client'} — ${formatCurrency(Number(o.total) || 0)}`,
    data: o
  };
}

const commandeAsyncStyles = {
  control: (base) => ({
    ...base,
    minHeight: 42,
    borderRadius: 12,
    borderColor: '#e2e8f0',
    boxShadow: 'none',
    '&:hover': { borderColor: '#cbd5e1' }
  }),
  menuPortal: (base) => ({ ...base, zIndex: 10060 })
};

function formatDateTime(v) {
  if (!v) return '—';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('fr-FR', {
    timeZone: TZ_CASA,
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

function roleLabel(role) {
  if (role === 'admin') return 'Admin';
  if (role === 'manager') return 'Manager';
  if (role === 'employee') return 'Employé';
  return role || '';
}

function canEditOrDeleteNote(user, row) {
  if (!user || !row) return false;
  if (user.role === 'admin') return true;
  if (user.role === 'manager' && Number(user.store_id) === Number(row.store_id)) return true;
  if (user.role === 'employee' && Number(row.created_by) === Number(user.id)) return true;
  return false;
}

function noteTiltDegrees(id, isPinned) {
  if (isPinned) return 0;
  const n = Number(id) || 0;
  return (((n * 13) % 9) - 4) * 0.55;
}

function noteStatusMeta(status) {
  return NOTE_STATUS[status] || NOTE_STATUS.pas_encore;
}

function NoteStatusPicker({ note, editable, saving, onChange, compact = false }) {
  const statusMeta = noteStatusMeta(note.status);
  const current = note.status && NOTE_STATUS[note.status] ? note.status : 'pas_encore';

  if (!editable) {
    return (
      <span className={`inline-flex px-2 py-0.5 rounded-md text-[10px] font-bold border ${statusMeta.badge}`}>
        {statusMeta.label}
      </span>
    );
  }

  return (
    <select
      value={current}
      disabled={saving}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
      onChange={(e) => {
        e.stopPropagation();
        onChange(e.target.value);
      }}
      className={`rounded-md border font-bold bg-white/90 text-amber-950 shadow-sm focus:ring-2 focus:ring-amber-400 focus:outline-none disabled:opacity-60 ${
        compact
          ? 'text-[10px] px-1.5 py-0.5 border-black/15 max-w-[7.5rem]'
          : 'text-xs px-2 py-1 border-black/20 min-w-[8.5rem]'
      } ${statusMeta.badge}`}
      aria-label="Changer le statut"
      title="Changer le statut"
    >
      {NOTE_STATUS_ORDER.map((key) => (
        <option key={key} value={key}>
          {NOTE_STATUS[key].label}
        </option>
      ))}
    </select>
  );
}

function NoteDetailView({ note, user, isAdmin, onClose, onEdit, archivedView = false, onStatusChange, statusSaving }) {
  const theme = NOTE_THEME[note.category] || NOTE_THEME.general;
  const editable = canEditOrDeleteNote(user, note);

  const handlePrint = () => {
    try {
      generateNoteTicketPDF(note);
    } catch {
      toast.error('Erreur lors de la génération du PDF');
    }
  };

  return (
    <div className="space-y-4 -mt-1">
      <div
        className={`rounded-2xl border border-black/10 bg-gradient-to-br ${theme.paper} ${theme.shadow} p-6 sm:p-8 relative overflow-hidden`}
      >
        <div
          className={`absolute top-4 left-1/2 -translate-x-1/2 w-6 h-6 rounded-full shadow-md border ${
            note.is_pinned
              ? 'bg-gradient-to-b from-amber-200 via-amber-400 to-amber-600 border-amber-800/25'
              : `bg-gradient-to-b ${theme.pin} border-black/10`
          }`}
          aria-hidden
        />
        <div className="pt-6 flex flex-wrap items-center gap-2 gap-y-2">
          <span
            className={`inline-flex px-2.5 py-1 rounded-lg text-xs font-bold uppercase tracking-wide border ${theme.badge}`}
          >
            {theme.label}
          </span>
          <NoteStatusPicker
            note={note}
            editable={editable}
            saving={statusSaving}
            onChange={(status) => onStatusChange(note, status)}
          />
          {archivedView && (
            <span className="text-xs font-bold text-emerald-900/80 bg-emerald-200/60 px-2 py-0.5 rounded-md border border-emerald-600/20">
              Archivée
            </span>
          )}
          {note.is_pinned && (
            <span className="text-xs font-bold text-amber-900/80 bg-amber-200/60 px-2 py-0.5 rounded-md border border-amber-600/20">
              Épinglée
            </span>
          )}
          {isAdmin() && note.store_name && (
            <span className="text-xs font-semibold text-black/50 flex items-center gap-1">
              <Store className="w-3.5 h-3.5" />
              {note.store_name}
            </span>
          )}
        </div>
        <h2 className="mt-4 text-2xl sm:text-3xl md:text-4xl font-bold text-amber-950/95 leading-tight pr-2">
          {note.title}
        </h2>
        <div className="mt-5 max-h-[min(65vh,28rem)] sm:max-h-[min(70vh,40rem)] overflow-y-auto overscroll-contain rounded-xl border border-black/5 bg-white/25 px-4 py-4 sm:px-6 sm:py-6">
          <p
            className="font-note text-2xl sm:text-3xl md:text-[1.85rem] leading-relaxed text-amber-950/95 whitespace-pre-wrap break-words"
            style={{ lineHeight: 1.45 }}
          >
            {note.body}
          </p>
        </div>
        {(note.client_name || note.client_phone) && (
          <div className="mt-5 pt-4 border-t border-black/10 text-base text-amber-950/90 space-y-2">
            {note.client_name && (
              <p className="flex items-center gap-2 font-semibold">
                <User className="w-4 h-4 opacity-50 shrink-0" />
                {note.client_name}
              </p>
            )}
            {note.client_phone && (
              <p className="flex items-center gap-2">
                <Phone className="w-4 h-4 opacity-50 shrink-0" />
                {note.client_phone}
              </p>
            )}
          </div>
        )}
        {note.sales_order_id ? (
          <div className="mt-4 flex items-center gap-2 text-base font-semibold text-sky-900">
            <Package className="w-4 h-4 shrink-0 opacity-70" />
            <Link to="/ventes/commandes" className="hover:underline underline-offset-2">
              Commande #{note.sales_order_id}
              {note.order_client_name ? ` · ${note.order_client_name}` : ''}
            </Link>
          </div>
        ) : null}
        <div className="mt-6 flex flex-wrap items-center justify-between gap-3 text-sm text-black/50 border-t border-black/10 pt-4">
          <span className="flex items-center gap-3 min-w-0">
            <span className="w-10 h-10 rounded-full bg-amber-950/90 text-amber-100 text-sm font-bold flex items-center justify-center shrink-0 shadow-inner">
              {(note.author_name || '?').charAt(0).toUpperCase()}
            </span>
            <span>
              <span className="font-semibold text-black/75 block">{note.author_name}</span>
              <span className="text-black/45">{roleLabel(note.author_role)}</span>
            </span>
          </span>
          <div className="text-right tabular-nums text-xs sm:text-sm">
            <div>Créée : {formatDateTime(note.created_at)}</div>
            {note.updated_at && note.updated_at !== note.created_at && (
              <div className="text-black/40 mt-0.5">Modifiée : {formatDateTime(note.updated_at)}</div>
            )}
          </div>
        </div>
      </div>
      <div className="flex flex-wrap justify-end gap-2 pt-1">
        <ModernButton type="button" variant="secondary" onClick={onClose} icon={<X className="w-4 h-4" />}>
          Fermer
        </ModernButton>
        <ModernButton
          type="button"
          variant="secondary"
          icon={<Printer className="w-4 h-4" />}
          onClick={handlePrint}
        >
          Imprimer
        </ModernButton>
        {editable && (
          <ModernButton
            type="button"
            variant="primary"
            className="!bg-amber-600 hover:!bg-amber-700"
            icon={<Pencil className="w-4 h-4" />}
            onClick={() => onEdit(note)}
          >
            Modifier
          </ModernButton>
        )}
      </div>
    </div>
  );
}

const Notes = () => {
  const { user, isAdmin } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stores, setStores] = useState([]);
  const [filterStoreId, setFilterStoreId] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [viewMode, setViewMode] = useState('active');

  const [modalOpen, setModalOpen] = useState(false);
  const [openedNote, setOpenedNote] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [commandeAsyncValue, setCommandeAsyncValue] = useState(null);
  const orderSearchTimerRef = useRef(null);
  const [statusSavingId, setStatusSavingId] = useState(null);

  const loadStores = useCallback(async () => {
    if (!isAdmin()) return;
    try {
      const res = await authAPI.getStores();
      setStores(res.data || []);
    } catch {
      setStores([]);
    }
  }, [isAdmin]);

  const loadNotes = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (viewMode === 'archived') {
        params.archived = 1;
      }
      if (isAdmin() && filterStoreId && filterStoreId !== 'all') {
        params.store_id = filterStoreId;
      }
      const res = await teamNotesAPI.list(params);
      setItems(res.data || []);
    } catch (e) {
      console.error(e);
      toast.error('Impossible de charger les notes');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [isAdmin, filterStoreId, viewMode]);

  useEffect(() => {
    loadStores();
  }, [loadStores]);

  useEffect(() => {
    loadNotes();
  }, [loadNotes]);

  useEffect(() => {
    setOpenedNote(null);
  }, [viewMode]);

  const loadOrderOptions = useCallback(
    (inputValue) =>
      new Promise((resolve) => {
        if (orderSearchTimerRef.current) clearTimeout(orderSearchTimerRef.current);
        orderSearchTimerRef.current = setTimeout(async () => {
          try {
            const sid = isAdmin() ? form.store_id : String(user?.store_id || '');
            if (isAdmin() && !sid) {
              resolve([]);
              return;
            }
            const params = { limit: 100 };
            const q = (inputValue || '').trim();
            if (q) params.search = q;
            if (isAdmin() && sid) params.store_id = sid;
            const res = await salesOrdersAPI.getSalesOrders(params);
            const rows = Array.isArray(res.data) ? res.data : res.data?.data || [];
            resolve(rows.map(orderToSelectOption).filter(Boolean));
          } catch {
            resolve([]);
          }
        }, 280);
      }),
    [isAdmin, form.store_id, user?.store_id]
  );

  const filteredItems = useMemo(() => {
    const q = searchTerm.toLowerCase().trim();
    return items.filter((n) => {
      if (categoryFilter !== 'all' && n.category !== categoryFilter) return false;
      if (!q) return true;
      return (
        String(n.title || '').toLowerCase().includes(q) ||
        String(n.body || '').toLowerCase().includes(q) ||
        String(n.client_name || '').toLowerCase().includes(q) ||
        String(n.client_phone || '').includes(searchTerm.trim()) ||
        String(n.author_name || '').toLowerCase().includes(q) ||
        (n.sales_order_id && String(n.sales_order_id).includes(q))
      );
    });
  }, [items, searchTerm, categoryFilter]);

  const openCreate = () => {
    setOpenedNote(null);
    setEditingId(null);
    setCommandeAsyncValue(null);
    setForm({
      ...emptyForm,
      store_id: isAdmin() ? (filterStoreId !== 'all' ? String(filterStoreId) : '') : String(user?.store_id || ''),
      is_pinned: false
    });
    setModalOpen(true);
  };

  const openEdit = (row) => {
    if (!canEditOrDeleteNote(user, row)) return;
    setOpenedNote(null);
    setEditingId(row.id);
    setCommandeAsyncValue(null);
    setForm({
      title: row.title || '',
      body: row.body || '',
      category: row.category && NOTE_THEME[row.category] ? row.category : 'general',
      status: row.status && NOTE_STATUS[row.status] ? row.status : 'pas_encore',
      client_name: row.client_name || '',
      client_phone: row.client_phone || '',
      sales_order_id: row.sales_order_id != null ? String(row.sales_order_id) : '',
      store_id: String(row.store_id || ''),
      is_pinned: !!row.is_pinned
    });
    setModalOpen(true);
    if (row.sales_order_id) {
      salesOrdersAPI
        .getSalesOrder(row.sales_order_id)
        .then((res) => {
          const opt = orderToSelectOption(res.data);
          if (opt) setCommandeAsyncValue(opt);
        })
        .catch(() => {
          setCommandeAsyncValue({
            value: String(row.sales_order_id),
            label: `Commande #${row.sales_order_id}`,
            data: null
          });
        });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      toast.error('Le titre est requis');
      return;
    }
    if (!form.body.trim()) {
      toast.error('Le texte de la note est requis');
      return;
    }
    if (isAdmin() && !editingId && !form.store_id) {
      toast.error('Choisissez un magasin');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        body: form.body.trim(),
        category: form.category,
        status: form.status,
        client_name: form.client_name.trim() || null,
        client_phone: form.client_phone.trim() || null,
        sales_order_id: form.sales_order_id ? Number(form.sales_order_id) : null,
        is_pinned: !!form.is_pinned
      };
      if (isAdmin() && !editingId) {
        payload.store_id = Number(form.store_id);
      }

      if (editingId) {
        await teamNotesAPI.update(editingId, payload);
        if (payload.status === 'traite') {
          toast.success('Note traitée et archivée');
        } else {
          toast.success('Note mise à jour');
        }
      } else {
        await teamNotesAPI.create(payload);
        if (payload.status === 'traite') {
          toast.success('Note publiée et archivée');
        } else {
          toast.success('Note publiée');
        }
      }
      setModalOpen(false);
      setForm(emptyForm);
      setEditingId(null);
      setCommandeAsyncValue(null);
      loadNotes();
    } catch (err) {
      const msg = err.response?.data?.errors?.[0]?.msg || err.response?.data?.message;
      toast.error(msg || 'Erreur lors de l’enregistrement');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row) => {
    if (!canEditOrDeleteNote(user, row)) return;
    if (!window.confirm(`Supprimer la note « ${row.title} » ?`)) return;
    try {
      await teamNotesAPI.remove(row.id);
      toast.success('Note supprimée');
      loadNotes();
    } catch {
      toast.error('Suppression impossible');
    }
  };

  const handlePrint = (row) => {
    try {
      generateNoteTicketPDF(row);
    } catch {
      toast.error('Erreur lors de la génération du PDF');
    }
  };

  const handleStatusChange = async (note, newStatus) => {
    if (!canEditOrDeleteNote(user, note)) return;
    const current = note.status && NOTE_STATUS[note.status] ? note.status : 'pas_encore';
    if (current === newStatus) return;

    setStatusSavingId(note.id);
    try {
      const res = await teamNotesAPI.updateStatus(note.id, { status: newStatus });
      const updated = res.data;

      if (newStatus === 'traite') {
        toast.success('Note traitée et archivée');
      } else {
        toast.success('Statut mis à jour');
      }

      if (openedNote?.id === note.id) {
        if (viewMode === 'active' && newStatus === 'traite') {
          setOpenedNote(null);
        } else {
          setOpenedNote(updated);
        }
      }

      await loadNotes();
    } catch {
      toast.error('Impossible de changer le statut');
    } finally {
      setStatusSavingId(null);
    }
  };

  /** Plein écran dans la zone contenu : annule le padding du Layout */
  const fullBleed =
    'w-[calc(100%+0.5rem*2)] max-w-none -mx-2 sm:w-[calc(100%+1rem*2)] sm:-mx-4 lg:w-[calc(100%+1.5rem*2)] lg:-mx-6';

  if (loading && items.length === 0) {
    return (
      <div
        className={`${fullBleed} min-h-[calc(100vh-5rem)] flex items-center justify-center`}
        style={BOARD_BG}
      >
        <div className="rounded-2xl bg-white/10 backdrop-blur-md px-8 py-10 border border-white/20 shadow-xl">
          <ModernLoadingSpinner size="lg" />
        </div>
      </div>
    );
  }

  return (
    <div
      className={`${fullBleed} min-h-[calc(100vh-5rem)] flex flex-col`}
      style={BOARD_BG}
    >
      {/* Barre d’outils — verre moderne */}
      <div className="shrink-0 z-20 border-b border-white/10 bg-gradient-to-b from-black/25 to-black/10 backdrop-blur-xl">
        <div className="px-3 sm:px-5 lg:px-8 py-4 sm:py-5">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
            <div className="flex items-start gap-3 sm:gap-4 min-w-0">
              <div className="hidden xs:flex shrink-0 h-12 w-12 sm:h-14 sm:w-14 rounded-2xl bg-gradient-to-br from-amber-200 to-amber-500 items-center justify-center shadow-lg shadow-black/20 border border-white/30">
                <StickyNote className="w-6 h-6 sm:w-7 sm:h-7 text-amber-950" strokeWidth={2} />
              </div>
              <div className="min-w-0">
                <p className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.2em] text-amber-100/90 mb-0.5">
                  Mur d’équipe
                </p>
                <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-white tracking-tight drop-shadow-sm">
                  Notes partagées
                </h1>
                <p className="mt-1 text-xs sm:text-sm text-amber-50/85 max-w-xl leading-snug">
                  {viewMode === 'archived'
                    ? 'Notes traitées — historique et suivi archivé.'
                    : 'Post-its collaboratifs — SAV, clients et suivi. Pensez à épingler l’essentiel.'}
                </p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-2 sm:gap-3">
              <div className="inline-flex rounded-xl border border-white/20 overflow-hidden bg-black/20">
                <button
                  type="button"
                  onClick={() => setViewMode('active')}
                  className={`px-3 py-2 text-xs sm:text-sm font-bold transition-colors ${
                    viewMode === 'active'
                      ? 'bg-white text-amber-950'
                      : 'text-white/85 hover:bg-white/10'
                  }`}
                >
                  Notes actives
                </button>
                <button
                  type="button"
                  onClick={() => setViewMode('archived')}
                  className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs sm:text-sm font-bold transition-colors border-l border-white/15 ${
                    viewMode === 'archived'
                      ? 'bg-white text-amber-950'
                      : 'text-white/85 hover:bg-white/10'
                  }`}
                >
                  <Archive className="w-4 h-4" />
                  Archives
                </button>
              </div>
              <div className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-xl bg-white/15 border border-white/20 text-sm text-white">
                <Sparkles className="w-4 h-4 text-amber-200 shrink-0" />
                <span className="font-bold tabular-nums">{filteredItems.length}</span>
                <span className="text-white/80 text-xs sm:text-sm">{viewMode === 'archived' ? 'archivée(s)' : 'note(s)'}</span>
              </div>
              {viewMode === 'active' && (
              <ModernButton
                variant="secondary"
                className="!bg-amber-300 !text-amber-950 hover:!bg-amber-200 !border-0 shadow-lg shadow-black/15 font-semibold w-full sm:w-auto justify-center"
                icon={<Plus className="w-4 h-4" />}
                onClick={openCreate}
              >
                Nouveau post-it
              </ModernButton>
              )}
            </div>
          </div>

          <div className="mt-4 rounded-2xl border border-white/15 bg-white/10 backdrop-blur-md p-3 sm:p-4 shadow-inner">
            <div className="flex flex-col lg:flex-row gap-3 lg:items-end">
              <div className="flex-1 min-w-0">
                <ModernInput
                  label="Recherche"
                  icon={<Search className="w-4 h-4 text-amber-100/70" />}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Titre, texte, client, auteur, n° commande…"
                  className="!bg-white/95 !border-white/40"
                />
              </div>
              {isAdmin() && (
                <div className="w-full lg:w-52">
                  <label className="block text-xs font-semibold text-amber-100/90 mb-1.5 uppercase tracking-wide">
                    Magasin
                  </label>
                  <div className="relative">
                    <Store className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-amber-900/50 pointer-events-none" />
                    <select
                      value={filterStoreId}
                      onChange={(e) => setFilterStoreId(e.target.value)}
                      className="w-full pl-10 pr-3 py-2.5 rounded-xl bg-white/95 border border-white/40 text-sm font-semibold text-amber-950 focus:ring-2 focus:ring-amber-300"
                    >
                      <option value="all">Tous les magasins</option>
                      {stores.map((s) => (
                        <option key={s.id} value={s.id}>
                          {s.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
            </div>
            <div className="mt-3 pt-3 border-t border-white/10">
              <p className="text-[10px] font-bold text-amber-100/80 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                <Filter className="w-3.5 h-3.5" />
                Catégorie
              </p>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setCategoryFilter('all')}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-all ${
                    categoryFilter === 'all'
                      ? 'bg-white text-amber-950 border-white shadow-md'
                      : 'bg-white/10 text-white border-white/25 hover:bg-white/20'
                  }`}
                >
                  Toutes
                </button>
                {CATEGORY_ORDER.map((key) => {
                  const meta = NOTE_THEME[key];
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setCategoryFilter(key)}
                      className={`px-3 py-1.5 rounded-full text-xs font-bold border transition-all ${
                        categoryFilter === key
                          ? 'bg-amber-200 text-amber-950 border-amber-100 shadow-md'
                          : 'bg-black/15 text-white border-white/20 hover:bg-black/25'
                      }`}
                    >
                      {meta.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Zone post-its — colonnes type tableau */}
      <div className="flex-1 px-3 sm:px-5 lg:px-8 py-6 sm:py-8 overflow-x-hidden">
        {filteredItems.length === 0 ? (
          <div className="max-w-md mx-auto mt-8 rounded-2xl border-2 border-dashed border-white/25 bg-white/5 backdrop-blur-sm p-10 text-center">
            <StickyNote className="w-14 h-14 text-amber-200/50 mx-auto mb-4" strokeWidth={1.25} />
            <p className="text-white font-semibold text-lg">
              {viewMode === 'archived' ? 'Aucune note archivée' : 'Aucun post-it ici'}
            </p>
            <p className="text-sm text-amber-100/75 mt-2">
              {viewMode === 'archived'
                ? 'Les notes marquées « Traité » apparaîtront ici.'
                : 'Changez les filtres ou ajoutez une note.'}
            </p>
            {viewMode === 'active' && (
            <ModernButton
              variant="secondary"
              className="mt-6 !bg-amber-300 !text-amber-950 hover:!bg-amber-200 border-0"
              icon={<Plus className="w-4 h-4" />}
              onClick={openCreate}
            >
              Coller une note
            </ModernButton>
            )}
          </div>
        ) : (
          <div
            className="columns-1 sm:columns-2 xl:columns-3 3xl:columns-4"
            style={{ columnGap: '1.25rem' }}
          >
            {filteredItems.map((n) => {
              const theme = NOTE_THEME[n.category] || NOTE_THEME.general;
              const editable = canEditOrDeleteNote(user, n);
              const tilt = noteTiltDegrees(n.id, !!n.is_pinned);
              return (
                <article
                  key={n.id}
                  className="break-inside-avoid mb-5 sm:mb-6 inline-block w-full align-top group relative"
                >
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => setOpenedNote(n)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setOpenedNote(n);
                      }
                    }}
                    className={`
                      relative rounded-sm overflow-visible pt-5 pb-4 px-4 sm:px-5
                      bg-gradient-to-br ${theme.paper} ${theme.shadow}
                      border border-black/[0.07]
                      transition-all duration-300 ease-out
                      hover:scale-[1.02] hover:rotate-0 hover:z-30 hover:shadow-[8px_12px_32px_rgba(0,0,0,0.28)]
                      cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400 focus-visible:ring-offset-2
                    `}
                    style={{
                      transform: `rotate(${tilt}deg)`,
                      transformOrigin: 'center top'
                    }}
                  >
                    {/* Punaise — dorée si épinglée */}
                    <div
                      className={`absolute -top-2 left-1/2 -translate-x-1/2 z-20 w-5 h-5 rounded-full shadow-[0_2px_4px_rgba(0,0,0,0.35),inset_0_1px_0_rgba(255,255,255,0.35)] border ${
                        n.is_pinned
                          ? 'bg-gradient-to-b from-amber-200 via-amber-400 to-amber-600 border-amber-800/25'
                          : `bg-gradient-to-b ${theme.pin} border-black/10`
                      }`}
                      title={n.is_pinned ? 'Note épinglée' : undefined}
                      aria-hidden
                    />

                    <div className="flex items-start justify-between gap-2 mb-2 pr-1">
                      <div className="flex flex-wrap items-center gap-1.5 min-w-0">
                      <span className={`inline-flex px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wide border ${theme.badge}`}>
                        {theme.label}
                      </span>
                      <NoteStatusPicker
                        note={n}
                        editable={editable}
                        saving={statusSavingId === n.id}
                        compact
                        onChange={(status) => handleStatusChange(n, status)}
                      />
                      </div>
                      {isAdmin() && (
                        <span className="text-[10px] font-semibold text-black/45 truncate max-w-[45%] flex items-center gap-1">
                          <Store className="w-3 h-3 shrink-0 opacity-60" />
                          {n.store_name}
                        </span>
                      )}
                    </div>

                    <div className="absolute top-8 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity z-10">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handlePrint(n);
                          }}
                          className="p-1.5 rounded-lg bg-white/80 hover:bg-white border border-black/10 text-amber-950 shadow-sm"
                          aria-label="Imprimer"
                          title="Imprimer"
                        >
                          <Printer className="w-3.5 h-3.5" />
                        </button>
                        {editable && (
                          <>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            openEdit(n);
                          }}
                          className="p-1.5 rounded-lg bg-white/80 hover:bg-white border border-black/10 text-amber-950 shadow-sm"
                          aria-label="Modifier"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(n);
                          }}
                          className="p-1.5 rounded-lg bg-white/80 hover:bg-red-50 border border-red-200/80 text-red-600 shadow-sm"
                          aria-label="Supprimer"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                          </>
                        )}
                      </div>

                    <h2 className="font-display font-bold text-lg sm:text-xl text-amber-950/95 leading-snug mb-2 pr-6 line-clamp-3">
                      {n.title}
                    </h2>
                    <p
                      className="font-note text-xl sm:text-2xl leading-snug text-amber-950/90 whitespace-pre-wrap line-clamp-[12]"
                      style={{ lineHeight: 1.35 }}
                    >
                      {n.body}
                    </p>

                    {(n.client_name || n.client_phone) && (
                      <div className="mt-4 pt-3 border-t border-black/10 text-sm font-sans text-amber-950/80 space-y-1">
                        {n.client_name && (
                          <p className="flex items-center gap-2 font-semibold">
                            <User className="w-3.5 h-3.5 opacity-50 shrink-0" />
                            {n.client_name}
                          </p>
                        )}
                        {n.client_phone && (
                          <p className="flex items-center gap-2">
                            <Phone className="w-3.5 h-3.5 opacity-50 shrink-0" />
                            {n.client_phone}
                          </p>
                        )}
                      </div>
                    )}

                    {n.sales_order_id ? (
                      <div
                        className="mt-3 flex items-center gap-2 text-sm font-sans font-semibold text-sky-900"
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => e.stopPropagation()}
                      >
                        <Package className="w-3.5 h-3.5 shrink-0 opacity-70" />
                        <Link to="/ventes/commandes" className="hover:underline underline-offset-2">
                          Commande #{n.sales_order_id}
                          {n.order_client_name ? ` · ${n.order_client_name}` : ''}
                        </Link>
                      </div>
                    ) : null}

                    <div className="mt-4 flex flex-wrap items-end justify-between gap-2 text-[11px] font-sans text-black/45">
                      <span className="flex items-center gap-2 min-w-0">
                        <span className="w-7 h-7 rounded-full bg-amber-950/90 text-amber-100 text-xs font-bold flex items-center justify-center shrink-0 shadow-inner">
                          {(n.author_name || '?').charAt(0).toUpperCase()}
                        </span>
                        <span className="min-w-0">
                          <span className="font-semibold text-black/70 block truncate">{n.author_name}</span>
                          <span className="text-black/40">{roleLabel(n.author_role)}</span>
                        </span>
                      </span>
                      <span className="tabular-nums">{formatDateTime(n.updated_at || n.created_at)}</span>
                    </div>

                    {/* Léger pli coin (effet papier) */}
                    <div
                      className="pointer-events-none absolute bottom-0 right-0 w-0 h-0 border-l-[14px] border-l-transparent border-b-[14px] border-b-black/10"
                      aria-hidden
                    />
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </div>

      <ModernModal
        isOpen={!!openedNote}
        onClose={() => setOpenedNote(null)}
        title="Lecture"
        size="xxl"
        className="!max-w-[min(52rem,calc(100vw-1.5rem))] !bg-neutral-50"
      >
        {openedNote && (
          <NoteDetailView
            note={openedNote}
            user={user}
            isAdmin={isAdmin}
            archivedView={viewMode === 'archived'}
            onClose={() => setOpenedNote(null)}
            onEdit={(row) => openEdit(row)}
            onStatusChange={handleStatusChange}
            statusSaving={statusSavingId === openedNote.id}
          />
        )}
      </ModernModal>

      <ModernModal
        isOpen={modalOpen}
        onClose={() => !saving && setModalOpen(false)}
        title={editingId ? 'Modifier le post-it' : 'Nouveau post-it'}
        size="xxl"
        className="!bg-white/98 !max-w-[min(56rem,calc(100vw-2rem))]"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          {isAdmin() && !editingId && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Magasin *</label>
              <select
                required
                value={form.store_id}
                onChange={(e) => {
                  const v = e.target.value;
                  setForm((f) => ({ ...f, store_id: v, sales_order_id: '' }));
                  setCommandeAsyncValue(null);
                }}
                className="w-full px-3 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-amber-500"
              >
                <option value="">— Choisir —</option>
                {stores.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <ModernInput
            label="Titre *"
            value={form.title}
            onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            required
          />

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Contenu *</label>
            <textarea
              value={form.body}
              onChange={(e) => setForm((f) => ({ ...f, body: e.target.value }))}
              rows={7}
              required
              className="w-full px-4 py-3 rounded-xl border-2 border-amber-200/80 bg-gradient-to-b from-[#fffef5] to-[#fef9c3] focus:ring-2 focus:ring-amber-400 focus:border-amber-400 text-xl sm:text-2xl font-note text-amber-950 leading-snug resize-y min-h-[160px] shadow-inner"
              placeholder="Écrivez comme sur un vrai post-it…"
              style={{
                lineHeight: 1.4,
                backgroundImage:
                  'repeating-linear-gradient(transparent, transparent 1.45rem, rgba(180,160,100,0.12) 1.45rem, rgba(180,160,100,0.12) calc(1.45rem + 1px))'
              }}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Catégorie (couleur du papier)</label>
            <select
              value={form.category}
              onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
              className="w-full px-3 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-amber-500"
            >
              {CATEGORY_ORDER.map((key) => (
                <option key={key} value={key}>
                  {NOTE_THEME[key].label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Statut</label>
            <select
              value={form.status}
              onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
              className="w-full px-3 py-2 border border-slate-200 rounded-xl focus:ring-2 focus:ring-amber-500"
            >
              {NOTE_STATUS_ORDER.map((key) => (
                <option key={key} value={key}>
                  {NOTE_STATUS[key].label}
                </option>
              ))}
            </select>
            {form.status === 'traite' && (
              <p className="mt-1 text-xs text-emerald-700">
                Une note « Traité » sera archivée et retirée du mur principal.
              </p>
            )}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <ModernInput
              label="Client (nom)"
              value={form.client_name}
              onChange={(e) => setForm((f) => ({ ...f, client_name: e.target.value }))}
              placeholder="Optionnel"
            />
            <ModernInput
              label="Client (téléphone)"
              value={form.client_phone}
              onChange={(e) => setForm((f) => ({ ...f, client_phone: e.target.value }))}
              placeholder="Optionnel"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Commande (optionnel)</label>
            <AsyncSelect
              key={isAdmin() ? `note-cmd-${form.store_id || 'x'}` : 'note-cmd'}
              instanceId="note-commande-async"
              cacheOptions
              defaultOptions
              loadOptions={loadOrderOptions}
              value={commandeAsyncValue}
              onChange={(opt) => {
                setForm((f) => ({ ...f, sales_order_id: opt?.value ?? '' }));
                setCommandeAsyncValue(opt || null);
              }}
              placeholder="Rechercher n° commande, client, téléphone…"
              isClearable
              isDisabled={isAdmin() && !form.store_id && !editingId}
              noOptionsMessage={({ inputValue }) => {
                if (isAdmin() && !form.store_id) return 'Choisissez d’abord un magasin';
                if ((inputValue || '').trim()) return 'Aucune commande trouvée';
                return 'Tapez pour rechercher';
              }}
              loadingMessage={() => 'Chargement…'}
              styles={commandeAsyncStyles}
              menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
              menuPosition="fixed"
            />
          </div>

          <label className="flex items-center gap-3 cursor-pointer select-none rounded-xl border border-amber-200/80 px-3 py-3 bg-amber-50/80">
            <input
              type="checkbox"
              checked={!!form.is_pinned}
              onChange={(e) => setForm((f) => ({ ...f, is_pinned: e.target.checked }))}
              className="rounded border-amber-400 text-amber-600 focus:ring-amber-500 w-4 h-4"
            />
            <span className="text-sm font-medium text-slate-800">Épingler (sans inclinaison, en évidence)</span>
          </label>

          <div className="flex justify-end gap-2 pt-4">
            <ModernButton type="button" variant="secondary" onClick={() => setModalOpen(false)} disabled={saving} icon={<X className="w-4 h-4" />}>
              Fermer
            </ModernButton>
            <ModernButton type="submit" variant="primary" disabled={saving} className="!bg-amber-600 hover:!bg-amber-700">
              {saving ? '…' : editingId ? 'Enregistrer' : 'Publier'}
            </ModernButton>
          </div>
        </form>
      </ModernModal>
    </div>
  );
};

export default Notes;
